"use client";
/*
 * Documentation:
 * Button — https://app.subframe.com/4f62853ee923/library?component=Button_3b777358-b86b-40af-9327-891efc6826fe
 * DC_Media_Figures — https://app.subframe.com/4f62853ee923/library?component=DC_Media_Figures_dbc283c5-897f-48b2-ad0b-db14a56e0cb0
 * Link Button — https://app.subframe.com/4f62853ee923/library?component=Link+Button_a4ee726a-774c-4091-8c49-55b659356024
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import * as SubframeUtils from "../utils";
import { Button } from "./Button";
import { LinkButton } from "./LinkButton";

interface ButtonStackProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "reading-comp" | "source-expand" | "expand" | "source" | "none";
  textButton?: React.ReactNode;
  sourceButton?: React.ReactNode;
  expandButton?: React.ReactNode;
  className?: string;
}

const ButtonStack = React.forwardRef<HTMLDivElement, ButtonStackProps>(
  function ButtonStack(
    {
      variant = "reading-comp",
      textButton,
      sourceButton,
      expandButton,
      className,
      ...otherProps
    }: ButtonStackProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/953875f9 flex w-full items-center justify-between",
          {
            "items-start justify-between":
              variant === "expand" || variant === "source-expand",
          },
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {textButton ? (
          <div
            className={SubframeUtils.twClassNames(
              "flex items-center justify-between",
              {
                hidden:
                  variant === "none" ||
                  variant === "source" ||
                  variant === "expand" ||
                  variant === "source-expand",
              }
            )}
          >
            {textButton}
          </div>
        ) : null}
        <div
          className={SubframeUtils.twClassNames("flex items-center gap-4", {
            hidden: variant === "none",
            "h-auto grow shrink-0 basis-0 items-start justify-end":
              variant === "expand",
            "h-auto grow shrink-0 basis-0 flex-row flex-nowrap justify-between":
              variant === "source-expand",
          })}
        >
          {sourceButton ? (
            <div
              className={SubframeUtils.twClassNames("flex items-center gap-4", {
                hidden: variant === "expand",
              })}
            >
              {sourceButton}
            </div>
          ) : null}
          {expandButton ? (
            <div
              className={SubframeUtils.twClassNames(
                "hidden items-center gap-4",
                {
                  hidden: variant === "source",
                  flex: variant === "expand" || variant === "source-expand",
                }
              )}
            >
              {expandButton}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
);

interface TextButtonProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
  className?: string;
}

const TextButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  TextButtonProps
>(function TextButton(
  { children, className, ...otherProps }: TextButtonProps,
  ref
) {
  return (
    <Button
      className={className}
      variant="brand-secondary"
      size="small"
      ref={ref}
      {...otherProps}
    >
      {children}
    </Button>
  );
});

interface ExpandButtonProps extends React.ComponentProps<typeof Button> {
  children?: React.ReactNode;
  className?: string;
}

const ExpandButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  ExpandButtonProps
>(function ExpandButton(
  { children, className, ...otherProps }: ExpandButtonProps,
  ref
) {
  return (
    <Button
      className={className}
      variant="brand-secondary"
      size="small"
      ref={ref}
      {...otherProps}
    >
      {children}
    </Button>
  );
});

interface SourceButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const SourceButton = React.forwardRef<HTMLDivElement, SourceButtonProps>(
  function SourceButton(
    { children, className, ...otherProps }: SourceButtonProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex items-center gap-2",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <LinkButton size="small" iconRight={<FeatherArrowUpRight />}>
          {children}
        </LinkButton>
      </div>
    );
  }
);

interface DescriptionProps extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  className?: string;
}

const Description = React.forwardRef<HTMLSpanElement, DescriptionProps>(
  function Description(
    { children, className, ...otherProps }: DescriptionProps,
    ref
  ) {
    return children ? (
      <span
        className={SubframeUtils.twClassNames(
          "w-full text-caption font-caption text-default-font",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children}
      </span>
    ) : null;
  }
);

interface SourceDescriptionProps extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  className?: string;
}

const SourceDescription = React.forwardRef<
  HTMLSpanElement,
  SourceDescriptionProps
>(function SourceDescription(
  { children, className, ...otherProps }: SourceDescriptionProps,
  ref
) {
  return children ? (
    <span
      className={SubframeUtils.twClassNames(
        "w-full text-caption font-caption text-subtext-color",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </span>
  ) : null;
});

interface Dc_Media_FiguresRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  noContainer?: boolean;
  image?: React.ReactNode;
  isPrint?: boolean;
  buttonStackSlot?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Dc_Media_FiguresRoot = React.forwardRef<
  HTMLDivElement,
  Dc_Media_FiguresRootProps
>(function Dc_Media_FiguresRoot(
  {
    noContainer = false,
    image,
    isPrint = false,
    buttonStackSlot,
    children,
    className,
    ...otherProps
  }: Dc_Media_FiguresRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/dbc283c5 flex w-full flex-col items-start gap-4 rounded-md bg-neutral-50 px-4 py-4",
        {
          "flex-col flex-nowrap gap-1 px-0 py-0": isPrint,
          "bg-transparent px-0 py-0": noContainer,
        },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {image ? (
        <div className="flex w-full flex-col items-start gap-4 overflow-hidden rounded-md">
          {image}
        </div>
      ) : null}
      {children ? (
        <div className="flex w-full flex-col items-start gap-1">{children}</div>
      ) : null}
      {buttonStackSlot ? (
        <div className="flex w-full flex-col items-start gap-6">
          {buttonStackSlot}
        </div>
      ) : null}
    </div>
  );
});

export const Dc_Media_Figures = Object.assign(Dc_Media_FiguresRoot, {
  ButtonStack,
  TextButton,
  ExpandButton,
  SourceButton,
  Description,
  SourceDescription,
});
